import { useEffect, useRef, useState } from "react";
import Masonry from "react-masonry-css";
import { AnimatePresence } from "framer-motion";
import tattooGalleryData from "./tattooGalleryData";
import TattooGalleryItem from "./TattooGalleryItem/TattooGalleryItem";
import TattooLightbox from "./TattooLightbox/TattooLightbox";
import styles from "./TattooGallery.module.css";

const breakpointColumnsObj = {
  default: 5,
  1200: 4,
  900: 3,
  600: 2,
  400: 1,
};

const INITIAL_COUNT = 8;
const LOAD_MORE_COUNT = 6;

const TattooGallery = () => {
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const [selectedImage, setSelectedImage] = useState(null);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        setVisibleCount((prev) => {
          if (prev >= tattooGalleryData.length) return prev;

          return Math.min(
            prev + LOAD_MORE_COUNT,
            tattooGalleryData.length
          );
        });
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedImage) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedImage(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage]);

  const visibleItems = tattooGalleryData
    .slice()
    .reverse()
    .slice(0, visibleCount);

  const hasMore = visibleCount < tattooGalleryData.length;

  return (
    <section className={styles.wrapper}>
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className={styles.masonryGrid}
        columnClassName={styles.masonryColumn}
      >
        {visibleItems.map((item, index) => (
          <TattooGalleryItem
            key={item.id}
            {...item}
            index={index}
            onClick={() => setSelectedImage(item)}
          />
        ))}
      </Masonry>

      {hasMore && (
        <div
          ref={loadMoreRef}
          className={styles.loadTrigger}
        />
      )}

      <AnimatePresence mode="sync">
        {selectedImage && (
          <TattooLightbox
            key={selectedImage.id}
            image={selectedImage}
            onClose={() => setSelectedImage(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default TattooGallery;